// The ONE name resolution for every lead provider (2026-10-07). Pure.
//
// Found on a real Anytime Fitness lead: its Meta form collects a FULL NAME
// ("Neha") and no first/last, so the lead arrived with both name fields blank.
// This runs in composeLead, BEFORE any provider composes and BEFORE any
// required-field check — a check that ran first would reject a lead for a
// missing first name we were about to derive. ⚠️ No provider implements its
// own split; Anytime Fitness had one and it is gone.
//
// THE SPLIT
//   · first AND last supplied → used as they are; the full name is not read.
//   · otherwise, a full name present → split on the FIRST run of whitespace:
//     the first word is the first name, EVERYTHING after it the last name
//     ("Mary Jane Van Der Berg" → "Mary" / "Jane Van Der Berg"). A full name
//     with no space is a first name only. A SUPPLIED value is never replaced —
//     the split only fills a field that was empty.
//   · neither → both empty. ⚠️ Never derived from the email address.
//
// THE PLACEHOLDER (Scott, blanket rule, every provider)
//   An empty first or last name is SENT as NAME_PLACEHOLDER — other CRMs reject
//   an empty name field. ⚠️⚠️ NEVER STORED: the submission keeps the field
//   empty, because our record says what the form gave us and a fabricated value
//   must never be indistinguishable from a supplied one. It is applied at the
//   transport edge only, through `outboundName`, and only to an EMPTY value.

import type { LeadInput, LeadNameField, LeadNameResolution, LeadSentFields } from '../../types/leadRelay.js';
import { anytimeFitnessSent, virtualYardSent } from './sent.js';

/** What an empty name is sent as. ONE constant; every transport reads it. */
export const NAME_PLACEHOLDER = 'NOT PROVIDED';

/**
 * The accepted key spellings, matched after trim + lowercase. ⚠️ An explicit
 * list, not a pattern: the real payload carries `full_name` AND `Full name`,
 * and interior whitespace is NOT collapsed, so each spelling is listed.
 */
export const FIRST_NAME_KEYS: readonly string[] = ['first_name', 'first name'];
export const LAST_NAME_KEYS: readonly string[] = ['last_name', 'last name'];
export const FULL_NAME_KEYS: readonly string[] = ['full_name', 'full name'];

function matchKey(key: string): string {
  return key.trim().toLowerCase();
}

/** The first non-empty value under any listed spelling, with the key it was found under. */
function lookup(input: LeadInput, keys: readonly string[]): { key: string; value: string } | null {
  for (const want of keys) {
    const direct = (input as Record<string, unknown>)[want];
    if (typeof direct === 'string' && direct.trim() !== '') return { key: want, value: direct.trim() };
    for (const [key, value] of Object.entries(input.extra ?? {})) {
      if (matchKey(key) === want && typeof value === 'string' && value.trim() !== '') return { key, value: value.trim() };
    }
  }
  return null;
}

/** Split on the FIRST run of whitespace; the rest, whole, is the last name. */
export function splitFullName(full: string): { first: string; last: string } {
  const trimmed = full.trim();
  const match = /\s+/.exec(trimmed);
  if (match === null) return { first: trimmed, last: '' };
  return { first: trimmed.slice(0, match.index), last: trimmed.slice(match.index + match[0].length).trim() };
}

export interface ResolvedLeadName {
  first: string;
  last: string;
  /** What happened, for the record — null when nothing worth telling a person did. */
  resolution: LeadNameResolution | null;
}

export function resolveLeadName(input: LeadInput): ResolvedLeadName {
  const suppliedFirst = lookup(input, FIRST_NAME_KEYS)?.value ?? '';
  const suppliedLast = lookup(input, LAST_NAME_KEYS)?.value ?? '';
  let first = suppliedFirst;
  let last = suppliedLast;
  let splitFrom: string | null = null;
  let fullName: string | null = null;
  const derived: LeadNameField[] = [];

  if (suppliedFirst === '' || suppliedLast === '') {
    const full = lookup(input, FULL_NAME_KEYS);
    if (full !== null) {
      const split = splitFullName(full.value);
      splitFrom = full.key;
      fullName = full.value;
      if (first === '' && split.first !== '') {
        first = split.first;
        derived.push('first_name');
      }
      if (last === '' && split.last !== '') {
        last = split.last;
        derived.push('last_name');
      }
    }
  }

  const placeholder: LeadNameField[] = [];
  if (first === '') placeholder.push('first_name');
  if (last === '') placeholder.push('last_name');

  const notable = splitFrom !== null || placeholder.length > 0;
  return {
    first,
    last,
    resolution: notable ? { splitFrom, fullName, derived, placeholder } : null,
  };
}

/** The input a provider composes from: the resolved names written in, nothing else changed. */
export function withResolvedName(input: LeadInput, name: ResolvedLeadName): LeadInput {
  const { first_name: _f, last_name: _l, ...rest } = input;
  return {
    ...rest,
    ...(name.first === '' ? {} : { first_name: name.first }),
    ...(name.last === '' ? {} : { last_name: name.last }),
  };
}

/** The value a transport sends for a name field. ⚠️ Transport edge only — never stored. */
export function outboundName(value: string): string {
  return value.trim() === '' ? NAME_PLACEHOLDER : value;
}

/**
 * A stored or typed name value, with the placeholder treated as ABSENT. ⚠️ The
 * placeholder is only ever sent, never a person's name — a lead with no name
 * anywhere must still read "(no name)", never "NOT PROVIDED".
 */
function storedName(value: string | undefined | null): string {
  const v = (value ?? '').trim();
  return v.toUpperCase() === NAME_PLACEHOLDER ? '' : v;
}

/**
 * THE ONE answer to "what is this lead's name", for every surface that shows
 * it (Lead Log row, detail panel, confirmations, Slack alert). Provider-agnostic:
 *   · what was COMPOSED for the provider, when there is one (VirtualYard's
 *     firstName/lastName, Anytime Fitness's prospectData) — already resolved;
 *   · otherwise the raw input through `resolveLeadName`, so a full_name-only
 *     lead that was never composed (unassigned) still splits.
 * Per field, so a composed-but-empty field falls back to the input.
 */
export function submissionLeadName(sub: {
  input?: LeadInput | null;
  sent?: LeadSentFields | null;
}): { first: string; last: string } {
  const vy = virtualYardSent(sub.sent);
  const af = anytimeFitnessSent(sub.sent)?.prospectData;
  const composedFirst = vy?.firstName ?? af?.first_name;
  const composedLast = vy?.lastName ?? af?.last_name;
  const resolved = sub.input ? resolveLeadName(sub.input) : { first: '', last: '' };
  return {
    first: storedName(composedFirst) || storedName(resolved.first),
    last: storedName(composedLast) || storedName(resolved.last),
  };
}
